import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Box,
    Chip,
    Typography,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputAdornment,
    IconButton,
    Divider,
    Switch,
    FormControlLabel,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { ArrowBack, Delete } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import {
    mediateEntriesInfoDependent,
    mediateUpdateEntriesRequester,
} from 'mediators';
import { UpdateEntriesSignal } from 'presentation/events';

const useStyles = makeStyles(() =>
    createStyles({
        root: { display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc' },
        topBar: {
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px',
            height: 48,
            background: 'white',
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
        },
        backBtn: { color: '#1e3a8a', marginRight: 8 },
        topTitle: { fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', flexGrow: 1 },
        body: { flex: 1, overflowY: 'auto', padding: '24px 20px' },
        form: { maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 16 },
        row: { display: 'flex', gap: 10 },
        actions: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 20px',
            background: 'white',
            borderTop: '1px solid #e2e8f0',
            flexShrink: 0,
        },
    })
);

export const EntryEditPage = () => {
    const classes = useStyles();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { pathname } = useLocation();
    const parentRoute = pathname.slice(0, pathname.lastIndexOf('/')) || '/entries';
    const dispatch = useDispatch();
    const entries: any[] = useSelector(mediateEntriesInfoDependent) || [];
    const allCategories: string[] = Array.from(new Set(entries.map((e: any) => e.category || e.tag).filter(Boolean))).sort() as string[];
    const allTags: string[] = Array.from(new Set(entries.filter((e: any) => e.category).map((e: any) => e.tag).filter(Boolean))).sort() as string[];

    const [updateEntriesSignal] = useState(new UpdateEntriesSignal());
    useEffect(() => mediateUpdateEntriesRequester(updateEntriesSignal, dispatch).destroy, [updateEntriesSignal, dispatch]);

    const entry = entries.find(e => e.id === id);

    const [title, setTitle] = useState('');
    const [amount, setAmount] = useState('');
    const [type, setType] = useState<'DEBIT' | 'CREDIT'>('DEBIT');
    const [category, setCategory] = useState('');
    const [tag, setTag] = useState('');
    const [date, setDate] = useState('');
    const [onCredit, setOnCredit] = useState(false);

    // Populate form once entry is available
    useEffect(() => {
        if (entry) {
            setTitle(entry.title || '');
            setAmount(String(entry.amount || ''));
            setType(entry.type || 'DEBIT');
            setCategory(entry.category || entry.tag || '');  // fallback: old entries store category in tag
            setTag(entry.category ? (entry.tag || '') : ''); // tag only meaningful once category exists
            setDate(entry.date ? String(entry.date).slice(0, 10) : '');
            setOnCredit(!!entry.onCredit);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entry?.id]);

    const goBack = () => history.push(parentRoute);

    const handleSave = () => {
        if (!entry) return;
        const updated = entries.map(e =>
            e.id === id
                ? { ...e, title, amount, type, category, tag: category ? tag : '', date: date || null, onCredit: type === 'DEBIT' ? onCredit : false }
                : e
        );
        updateEntriesSignal.dispatch(updated);
        goBack();
    };

    const handleDelete = () => {
        updateEntriesSignal.dispatch(entries.filter(e => e.id !== id));
        goBack();
    };

    if (!entry && entries.length > 0) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" style={{ height: '100%' }}>
                <Typography style={{ color: '#94a3b8', marginBottom: 16 }}>Entry not found.</Typography>
                <Button variant="outlined" onClick={goBack}>Back to entries</Button>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            {/* Top bar */}
            <Box className={classes.topBar}>
                <IconButton size="small" className={classes.backBtn} onClick={goBack}>
                    <ArrowBack />
                </IconButton>
                <Typography className={classes.topTitle}>Edit Entry</Typography>
            </Box>

            {/* Form body */}
            <Box className={classes.body}>
                <Box className={classes.form}>
                    <TextField
                        label="Description"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoFocus
                    />

                    <Box className={classes.row}>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
                            <Select
                                value={type}
                                onChange={e => setType(e.target.value as 'DEBIT' | 'CREDIT')}
                            >
                                <MenuItem value="DEBIT">Debit (−)</MenuItem>
                                <MenuItem value="CREDIT">Credit (+)</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Amount"
                            type="number"
                            value={amount}
                            onChange={e => setAmount(e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Box>

                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={allCategories}
                        value={category ? category.split(',').map(s => s.trim()).filter(Boolean) : []}
                        onChange={(_, newVal) => setCategory((newVal as string[]).join(','))}
                        renderTags={(val, getTagProps) =>
                            val.map((opt, i) => (
                                <Chip
                                    label={opt}
                                    size="small"
                                    color="primary"
                                    style={{ fontSize: '0.75rem', height: 24 }}
                                    {...getTagProps({ index: i })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Category" variant="outlined" placeholder="Type to search…" />
                        )}
                    />

                    <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={allTags}
                        value={tag ? tag.split(',').map(s => s.trim()).filter(Boolean) : []}
                        onChange={(_, newVal) => setTag((newVal as string[]).join(','))}
                        renderTags={(val, getTagProps) =>
                            val.map((opt, i) => (
                                <Chip
                                    label={opt}
                                    size="small"
                                    color="primary"
                                    style={{ fontSize: '0.75rem', height: 24 }}
                                    {...getTagProps({ index: i })}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Tag" variant="outlined" placeholder="Type to search…" />
                        )}
                    />

                    <TextField
                        label="Date"
                        type="date"
                        value={date}
                        onChange={e => setDate(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                    />

                    {type === 'DEBIT' && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onCredit}
                                    onChange={e => setOnCredit(e.target.checked)}
                                    size="small"
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: onCredit ? '#6d28d9' : '#475569' }}>
                                        On credit (credit card)
                                    </Typography>
                                    <Typography style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                                        Won't reduce cash balance in projection
                                    </Typography>
                                </Box>
                            }
                        />
                    )}

                    <Divider style={{ margin: '8px 0' }} />

                    <Button
                        startIcon={<Delete />}
                        style={{ color: '#dc2626', alignSelf: 'flex-start', textTransform: 'none' }}
                        onClick={handleDelete}
                    >
                        Delete entry
                    </Button>
                </Box>
            </Box>

            {/* Action bar */}
            <Box className={classes.actions}>
                <Button onClick={goBack} style={{ color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!title.trim() || !amount}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};
